import React, { useCallback, useEffect, useState } from 'react'
import {
	Title,
	Grid,
	Button,
	Card,
	Group,
	Text,
	Loader,
	Table,
	Box,
	Select,
	Paper,
	Image,
	Anchor,
	createStyles,
	useMantineTheme,
} from '@mantine/core'
import { getExperienceStats } from '../services/actions/experienceActions'
import { useNavigate, useParams } from 'react-router-dom'
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
} from 'recharts'

const useStyles = createStyles((theme) => ({
	page: {
		minWidth: 0,
		[theme.fn.smallerThan('sm')]: { padding: 0 },
	},
	header: {
		[theme.fn.smallerThan('sm')]: {
			alignItems: 'stretch',
			flexDirection: 'column',
			gap: theme.spacing.sm,
		},
	},
	panel: {
		minWidth: 0,
		[theme.fn.smallerThan('sm')]: { padding: theme.spacing.xs },
	},
	table: {
		tableLayout: 'fixed',
		'&& th, && td': { overflowWrap: 'anywhere' },
		[theme.fn.smallerThan('sm')]: {
			'&& th, && td': { padding: '8px 4px', fontSize: theme.fontSizes.xs },
		},
	},
	itemsTable: {
		'& col:nth-of-type(1)': { width: '32%' },
		'& col:nth-of-type(2)': { width: '23%' },
		'& col:nth-of-type(n + 3)': { width: '15%' },
		[theme.fn.smallerThan('md')]: {
			'& col:nth-of-type(1)': { width: '33%' },
			'& col:nth-of-type(3), & col:nth-of-type(4)': { width: '20%' },
			'& col:nth-of-type(5)': { width: '27%' },
		},
	},
	imageColumn: {
		[theme.fn.smallerThan('md')]: { display: 'none' },
	},
}))

const USER_PREVIEW_LIMIT = 5

const ExperienceOverviewPage = () => {
	const theme = useMantineTheme()
	const isDark = theme.colorScheme === 'dark'
	const { classes, cx } = useStyles()
	const { experienceSlug, orgSlug } = useParams()
	const navigate = useNavigate()
	const [stats, setStats] = useState({})
	const [loading, setLoading] = useState(true)
	const [selectedStation, setSelectedStation] = useState('Total')
	const [showAllUsers, setShowAllUsers] = useState(false)

	const fetchStats = useCallback(async () => {
		try {
			const data = await getExperienceStats(experienceSlug, orgSlug)

			if (Array.isArray(data.checkInsByHour)) {
				data.checkInsByHour = data.checkInsByHour.map(
					({ hour, label, count }) => ({
						hour,
						label,
						count,
					}),
				)
			}

			setStats(data)
		} catch (err) {
			console.error(err)
		} finally {
			setLoading(false)
		}
	}, [experienceSlug, orgSlug])

	useEffect(() => {
		fetchStats()
		const interval = setInterval(fetchStats, 30000) // Refresh every 30 seconds
		return () => clearInterval(interval)
	}, [fetchStats])

	const users = stats.topUsers || []
	const visibleUsers = showAllUsers ? users : users.slice(0, USER_PREVIEW_LIMIT)
	const handleUserSelect = (userId) => {
		const query = new URLSearchParams({ userId })
		navigate(`/${orgSlug}/experiences/${experienceSlug}/history?${query}`)
	}

	const stationOptions = Object.keys(stats?.itemsByStation || {}).map(
		(station) => ({
			label: station,
			value: station,
		}),
	)

	const itemRows =
		stats?.itemsByStation?.[selectedStation]
			?.map((item) => {
				const remaining = item.totalAttached - item.totalIssued
				const percentComplete =
					item.totalAttached > 0
						? Math.round((item.totalIssued / item.totalAttached) * 100)
						: 0

				return {
					itemName: item.itemName,
					imageUrl: item.imageUrl,
					attached: item.totalAttached,
					issued: item.totalIssued,
					remaining,
					percentComplete,
				}
			})
			.sort((a, b) => a.itemName.localeCompare(b.itemName)) || []

	const itemTotals = itemRows.reduce(
		(totals, item) => ({
			attached: totals.attached + item.attached,
			issued: totals.issued + item.issued,
			remaining: totals.remaining + item.remaining,
		}),
		{ attached: 0, issued: 0, remaining: 0 },
	)

	return (
		<Box p='md' className={classes.page}>
			<Group position='apart' mb='md' className={classes.header}>
				<Title order={2}>Check-In Overview</Title>
				<Button
					variant='outline'
					onClick={() =>
						navigate(`/${orgSlug}/experiences/${experienceSlug}/overview/sales`)
					}
				>
					Sales Overview
				</Button>
			</Group>

			{loading ? (
				<Loader />
			) : Object.keys(stats).length === 0 ? (
				<Text>Error loading stats</Text>
			) : (
				<>
					<Grid>
						<Grid.Col xs={12} sm={6} md={3}>
							<Card shadow='sm' withBorder h='100%'>
								<Text size='sm' weight={500}>
									Total Check-Ins Completed
								</Text>
								<Title>{stats.checkInsCompletedTotal}</Title>
							</Card>
						</Grid.Col>
						<Grid.Col xs={12} sm={6} md={3}>
							<Card shadow='sm' withBorder h='100%'>
								<Text size='sm' weight={500}>
									Total Check-Ins Remaining
								</Text>
								<Title>{stats.checkInsRemainingTotal}</Title>
							</Card>
						</Grid.Col>
						<Grid.Col xs={12} sm={6} md={3}>
							<Card shadow='sm' withBorder h='100%'>
								<Text size='sm' weight={500}>
									% Complete Overall
								</Text>
								<Title>{stats.checkInCompletionRateTotal}%</Title>
							</Card>
						</Grid.Col>
						<Grid.Col xs={12} sm={6} md={3}>
							<Card shadow='sm' withBorder h='100%'>
								<Text size='sm' weight={500}>
									Current Check-In Rate
								</Text>
								<Title>{stats.currentCheckInRatePerHour ?? 0}/hour</Title>
								<Text size='xs' color='dimmed'>
									Based on the last {stats.currentCheckInRateWindowMinutes ?? 10}{' '}
									minutes
								</Text>
							</Card>
						</Grid.Col>
					</Grid>

					{stats.checkInsByHour?.length > 0 && (
						<Paper className={classes.panel} shadow='xs' p='md' withBorder mb='md' mt='md'>
							<Title order={3} mb='sm'>
								Check-Ins Per Hour
							</Title>
							<ResponsiveContainer width='100%' height={250}>
								<LineChart data={stats.checkInsByHour}>
									<XAxis dataKey='label' />
									<YAxis allowDecimals={false} />
									<Tooltip
										contentStyle={{
											backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
											borderColor: isDark ? theme.colors.dark[4] : theme.colors.gray[3],
											borderRadius: theme.radius.sm,
										}}
										labelStyle={{ color: isDark ? theme.colors.dark[0] : theme.black }}
										itemStyle={{ color: isDark ? theme.colors.blue[3] : theme.colors.blue[6] }}
									/>
									<Line
										type='monotone'
										dataKey='count'
										stroke='#228be6'
										strokeWidth={2}
									/>
								</LineChart>
							</ResponsiveContainer>
						</Paper>
					)}

					<Grid mt='xl'>
						{users.length > 0 && (
							<Grid.Col xs={12} md={6}>
								<Paper className={classes.panel} shadow='xs' p='md' withBorder>
									<Title order={4} mb='sm'>
										Check-Ins by User
									</Title>
									<Table className={classes.table} highlightOnHover>
										<thead>
											<tr>
												<th>Name</th>
												<th>Check-Ins</th>
											</tr>
										</thead>
										<tbody>
											{visibleUsers.map((user) => (
												<tr
													key={user.userId}
													role='link'
													tabIndex={0}
													aria-label={`View ${user.name || 'Unknown user'}'s check-in history`}
													style={{ cursor: 'pointer' }}
													onClick={() => handleUserSelect(user.userId)}
													onKeyDown={(event) => {
														if (event.key === 'Enter') {
															event.preventDefault()
															handleUserSelect(user.userId)
														}
													}}
												>
													<td>{user.name || 'Unknown user'}</td>
													<td>{user.count}</td>
												</tr>
											))}
										</tbody>
									</Table>
									{users.length > USER_PREVIEW_LIMIT && (
										<Anchor
											component='button'
											color='blue'
											size='sm'
											weight={500}
											mt='sm'
											aria-expanded={showAllUsers}
											onClick={() => setShowAllUsers((value) => !value)}
										>
											{showAllUsers ? 'Show less' : 'Show all'}
										</Anchor>
									)}
								</Paper>
							</Grid.Col>
						)}

						{stats.stationProgress?.length > 0 && (
							<Grid.Col xs={12} md={6}>
								<Paper className={classes.panel} shadow='xs' p='md' withBorder>
									<Title order={4} mb='sm'>
										Station Progress
									</Title>
									<Table className={classes.table} highlightOnHover>
										<thead>
											<tr>
												<th>Station</th>
												<th>Completed</th>
												<th>Remaining</th>
											</tr>
										</thead>
										<tbody>
											{stats.stationProgress?.map((s) => (
												<tr key={s.station}>
													<td>{s.stationName}</td>
													<td>{s.completedTotal}</td>
													<td>{s.remainingTotal}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</Paper>
							</Grid.Col>
						)}
					</Grid>

					{stationOptions?.length > 0 && (
						<Paper className={classes.panel} shadow='xs' p='md' withBorder mt='xl'>
							<Title order={4} mb='sm'>
								Items by Station
							</Title>

							<Select
								label='Select Station'
								data={stationOptions}
								value={selectedStation}
								onChange={setSelectedStation}
								mb='md'
								withinPortal
							/>

							<Table className={cx(classes.table, classes.itemsTable)} highlightOnHover>
								<colgroup>
									<col />
									<col className={classes.imageColumn} />
									<col />
									<col />
									<col />
								</colgroup>
								<thead>
									<tr>
										<th>Item</th>
										<th className={classes.imageColumn} style={{ textAlign: 'center' }}>Image</th>
										<th>Total</th>
										<th>Issued</th>
										<th>Remaining</th>
									</tr>
								</thead>
								<tbody>
									{itemRows.map((item) => (
										<tr key={item.itemName}>
											<td>{item.itemName}</td>
											<td className={classes.imageColumn} style={{ textAlign: 'center' }}>
												{item.imageUrl ? (
													<Image
														src={item.imageUrl}
														alt={item.itemName}
														width='100%'
														maw={140}
														height={32}
														mx='auto'
														fit='contain'
														withPlaceholder
														placeholder={<Text color='dimmed' size='sm'>—</Text>}
													/>
												) : (
													<Text color='dimmed' size='sm'>
														—
													</Text>
												)}
											</td>
											<td>{item.attached}</td>
											<td>{item.issued}</td>
											<td>{item.remaining}</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr>
										<th scope='row'>
											Total
										</th>
										<th className={classes.imageColumn} aria-hidden='true' />
										<th>{itemTotals.attached}</th>
										<th>{itemTotals.issued}</th>
										<th>{itemTotals.remaining}</th>
									</tr>
								</tfoot>
							</Table>
						</Paper>
					)}
				</>
			)}
		</Box>
	)
}

export default ExperienceOverviewPage
